import { UploadingFile } from "@/components/Conversation/FileUploadTypes"
import type { HttpRequestToolConfig } from "./httpRequestTool"
import type { RequestFormSchema } from "./requestForm"
import type { Text2SpeechConfig } from "./text2speechTool"

export const enum AgentType {
  Basic = "basic",
}

export type AgentResponseMode = "ai" | "human_only"
export type DraftPolicy = "auto" | "approval_required"

export const DEFAULT_AGENT_RESPONSE_MODE: AgentResponseMode = "ai"

export type UnifiedToolType =
  | "knowledge_base"
  | "web_search"
  | "call_agent"
  | "image_generation"
  | "request_form"
  | "payment"
  | "memory"
  | "http_request"
  | "text2speech"
  | "tabular_data"

export type AgentThinkingLevel = "minimal" | "low" | "medium" | "high" | "xhigh"

export interface AgentLLMModelSettings {
  temperature?: number
  maxTokens?: number
  topP?: number
  thinking?: AgentThinkingLevel
}

export type AgentLLMModelSettingsMap = Record<string, AgentLLMModelSettings>

export interface UnifiedTool {
  id: string
  type: UnifiedToolType
  name?: string
  description?: string
  version?: number
  domain?: string
  invocationInstruction?: string
  draftPolicy?: DraftPolicy
  agentId?: string
  knowledgeNodeIds?: string[]
  knowledgeNodeId?: string
  imageModel?: string
  customFormSchema?: RequestFormSchema
  httpRequest?: HttpRequestToolConfig
  text2speech?: Text2SpeechConfig
}

export interface AgentSuggestion {
  description?: string
}

export interface ApiAttachment {
  id: string
  original_name: string
  content_type: string
  size: number
  source_url: string | null
  provider: string | null
  scope: string
  created_at: string
}

export interface HumanHandoffConfig {
  enabled: boolean
  idleTimeoutMinutes?: number | null
  handoffInstructions?: string
}

export const DEFAULT_HUMAN_HANDOFF_CONFIG: HumanHandoffConfig = {
  enabled: false,
}

// IMPORTANT: Keep in sync with AgentMeta.ai_takeover_greeting max length in codeer/agents.
export const MAX_AI_TAKEOVER_GREETING_LENGTH = 1000

/**
 * The greeting is sent verbatim, so only whitespace-only values are normalized
 * (to "", which disables the greeting). Non-string values are treated as unset.
 */
export const normalizeAiTakeoverGreeting = (value: unknown): string =>
  typeof value === "string" && value.trim().length > 0 ? value : ""

export type OutputPresentationKind =
  | "citation_list"
  | "card_carousel"
  | "image_gallery"
  | "action_panel"
  | "list_digest"
  | "status_timeline"
  | "location_carousel"

export interface OutputPresentationPolicy {
  enableOutputPresentations: boolean
  allowedPresentationKinds: OutputPresentationKind[]
  maxItemsByKind: Partial<Record<OutputPresentationKind, number>>
  whenToUseByKind: Partial<Record<OutputPresentationKind, string>>
}

export const OUTPUT_PRESENTATION_KINDS: Array<{
  kind: OutputPresentationKind
  defaultMaxItems: number
  maxItemLimit: number
}> = [
  { kind: "citation_list", defaultMaxItems: 5, maxItemLimit: 5 },
  { kind: "card_carousel", defaultMaxItems: 8, maxItemLimit: 10 },
  { kind: "image_gallery", defaultMaxItems: 8, maxItemLimit: 20 },
  { kind: "action_panel", defaultMaxItems: 1, maxItemLimit: 1 },
  { kind: "list_digest", defaultMaxItems: 1, maxItemLimit: 1 },
  { kind: "status_timeline", defaultMaxItems: 1, maxItemLimit: 1 },
  { kind: "location_carousel", defaultMaxItems: 8, maxItemLimit: 10 },
]

export const OUTPUT_PRESENTATION_KIND_LIMITS = Object.fromEntries(
  OUTPUT_PRESENTATION_KINDS.map(({ kind, defaultMaxItems, maxItemLimit }) => [
    kind,
    { defaultMaxItems, maxItemLimit },
  ])
) as Record<
  OutputPresentationKind,
  { defaultMaxItems: number; maxItemLimit: number }
>

export const clampOutputPresentationMaxItems = (
  kind: OutputPresentationKind,
  value: number | undefined
) => {
  const limits = OUTPUT_PRESENTATION_KIND_LIMITS[kind]
  const numericValue =
    typeof value === "number" && Number.isFinite(value)
      ? Math.trunc(value)
      : limits.defaultMaxItems
  return Math.max(1, Math.min(limits.maxItemLimit, numericValue))
}

export const DEFAULT_OUTPUT_PRESENTATION_POLICY: OutputPresentationPolicy = {
  enableOutputPresentations: false,
  allowedPresentationKinds: [],
  maxItemsByKind: {},
  whenToUseByKind: {},
}

export interface AgentHistory {
  id: string
  agentId: string
  versionNumber: number
  name: string
  description?: string
  versionNote?: string
  systemPrompt: string
  meta: Record<string, unknown>
  unifiedTools: UnifiedTool[]
  suggestedQuestions?: string[]
  responseMode: AgentResponseMode
  humanHandoff: HumanHandoffConfig
  aiTakeoverGreeting?: string
  useSearch: boolean
  llmModel?: string
  llmModelSettings?: AgentLLMModelSettingsMap
  outputPresentationPolicy?: OutputPresentationPolicy
  attachments?: UploadingFile[]
  createdBy?: {
    id: string
    name: string
    email: string
  }
  createdAt: string
  status: "draft" | "published" | "archived"
  wasPublished: boolean
  publishedAt?: string
  supportsImageRegionEditing: boolean
}
